import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Flame } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PRIMECanvas {
  // PRIME
  porosity: string;
  resistance: string;
  integrity: string;
  melaninBase: string;
  endGoal: string;
  // CANVAS
  currentLevel: string;
  artificialPigment: string;
  naturalBase: string;
  visualTexture: string;
  aim: string;
  strategy: string;
}

interface Option { value: string; label: string }

// ── Option sets ──────────────────────────────────────────────────────────────

// PRIME options
const POROSITY_OPTS: Option[] = [
  { value: 'low',     label: 'Low — resistant, slow to absorb' },
  { value: 'normal',  label: 'Normal — balanced absorption' },
  { value: 'high',    label: 'High — very porous, absorbs fast' },
  { value: 'unknown', label: 'Not sure yet' },
];

const RESISTANCE_OPTS: Option[] = [
  { value: 'virgin',     label: 'Virgin / natural — never processed' },
  { value: 'previously', label: 'Previously colored' },
  { value: 'highlighted',label: 'Previously highlighted / lightened' },
  { value: 'vivid',      label: 'Has vivid or fashion color' },
  { value: 'unknown',    label: 'History unknown' },
];

const INTEGRITY_OPTS: Option[] = [
  { value: 'healthy',    label: 'Healthy — no visible damage' },
  { value: 'mild',       label: 'Mild damage — slightly dry' },
  { value: 'moderate',   label: 'Moderate damage — brittle or breaking' },
  { value: 'severe',     label: 'Severe damage — do not process yet' },
];

const MELANIN_OPTS: Option[] = [
  { value: '1-2',  label: 'Level 1–2 · Deepest black / very dark' },
  { value: '3-4',  label: 'Level 3–4 · Dark brown' },
  { value: '5-6',  label: 'Level 5–6 · Medium to light brown' },
  { value: '7-8',  label: 'Level 7–8 · Dark to medium blonde' },
  { value: '9-10', label: 'Level 9–10 · Light to very light blonde' },
  { value: 'gray', label: 'Gray / white' },
  { value: 'unknown', label: 'Not sure of level' },
];

const GOAL_OPTS: Option[] = [
  { value: 'lift',       label: 'Lift — go lighter' },
  { value: 'deposit',    label: 'Deposit — add tone / darken' },
  { value: 'tone',       label: 'Tone — neutralize or refine' },
  { value: 'fill',       label: 'Fill — rebuild warmth / pigment' },
  { value: 'correct',    label: 'Correct — fix a previous result' },
  { value: 'neutralize', label: 'Neutralize — cancel brassiness' },
  { value: 'cut',        label: 'Cut / shape only' },
];

// CANVAS options
const CURRENT_LEVEL_OPTS: Option[] = [
  { value: '1-2',  label: 'Level 1–2 · Very dark' },
  { value: '3-4',  label: 'Level 3–4 · Dark brown' },
  { value: '5-6',  label: 'Level 5–6 · Medium brown' },
  { value: '7-8',  label: 'Level 7–8 · Blonde range' },
  { value: '9-10', label: 'Level 9–10 · Light blonde' },
  { value: '11-12', label: 'Level 11–12 · Palest blonde' },
  { value: 'mixed', label: 'Mixed levels present' },
];

const ARTIFICIAL_PIGMENT_OPTS: Option[] = [
  { value: 'none',     label: 'None — virgin hair' },
  { value: 'warm',     label: 'Warm tones present (red, copper, gold)' },
  { value: 'cool',     label: 'Cool tones present (ash, violet, blue)' },
  { value: 'fashion',  label: 'Fashion color present (vivid / pastel)' },
  { value: 'buildup',  label: 'Color buildup — multiple processes' },
  { value: 'unknown',  label: 'Not sure what\'s in the hair' },
];

const NATURAL_BASE_OPTS: Option[] = [
  { value: '1-2',  label: 'Level 1–2 · Deep red underlying pigment' },
  { value: '3-4',  label: 'Level 3–4 · Red to red-orange' },
  { value: '5-6',  label: 'Level 5–6 · Orange to yellow-orange' },
  { value: '7-8',  label: 'Level 7–8 · Yellow-orange to yellow' },
  { value: '9-10', label: 'Level 9–10 · Pale yellow' },
  { value: 'gray', label: 'Gray / white — no melanin' },
];

const VISUAL_TEXTURE_OPTS: Option[] = [
  { value: 'fine',     label: 'Fine — delicate, processes fast' },
  { value: 'medium',   label: 'Medium — standard processing' },
  { value: 'coarse',   label: 'Coarse — resistant, needs more time' },
  { value: 'curly',    label: 'Curly / coily — fragile when processed' },
  { value: 'mixed',    label: 'Mixed textures present' },
];

const AIM_OPTS: Option[] = [
  { value: 'lighter',     label: 'Go lighter — lift to target level' },
  { value: 'darker',      label: 'Go darker — deposit or fill' },
  { value: 'same-level',  label: 'Same level — shift tone only' },
  { value: 'correct',     label: 'Correct a previous result' },
  { value: 'blend-gray',  label: 'Blend or cover gray' },
  { value: 'creative',    label: 'Creative / fashion color goal' },
];

const STRATEGY_OPTS: Option[] = [
  { value: 'single',    label: 'Single process — one step' },
  { value: 'multi',     label: 'Multi-step — lift then tone' },
  { value: 'staged',    label: 'Staged plan — multiple sessions' },
  { value: 'corrective',label: 'Corrective — fix then rebuild' },
  { value: 'consult',   label: 'Need help deciding' },
];

// ── Select row ───────────────────────────────────────────────────────────────

function SelectRow({
  label,
  letter,
  options,
  value,
  onChange,
}: {
  label: string;
  letter: string;
  options: Option[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-primary font-display text-[10px] font-bold text-primary-foreground leading-none">
          {letter}
        </span>
        <p className="font-body text-xs font-semibold text-foreground uppercase tracking-widest">{label}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(active ? '' : opt.value)}
              className={`rounded-full border px-3 py-1 font-body text-[11px] transition-all active:scale-95 ${
                active
                  ? 'border-primary/60 bg-primary/10 text-primary font-semibold'
                  : 'border-border bg-background text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Summary badge (collapsed state) ─────────────────────────────────────────

function SummaryBadges({ canvas }: { canvas: PRIMECanvas }) {
  const filled = Object.values(canvas).filter(Boolean);
  if (!filled.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {Object.entries(canvas).map(([k, v]) =>
        v ? (
          <span key={k} className="rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 font-body text-[10px] text-primary">
            {v}
          </span>
        ) : null
      )}
    </div>
  );
}

// ── Main widget ──────────────────────────────────────────────────────────────

interface PRIMEIntakeWidgetProps {
  canvas: PRIMECanvas;
  onChange: (canvas: PRIMECanvas) => void;
}

export function PRIMEIntakeWidget({ canvas, onChange }: PRIMEIntakeWidgetProps) {
  const [open, setOpen] = useState(false);

  const set = (key: keyof PRIMECanvas) => (v: string) =>
    onChange({ ...canvas, [key]: v });

  const primeKeys: (keyof PRIMECanvas)[] = ['porosity', 'resistance', 'integrity', 'melaninBase', 'endGoal'];
  const canvasKeys: (keyof PRIMECanvas)[] = ['currentLevel', 'artificialPigment', 'naturalBase', 'visualTexture', 'aim', 'strategy'];
  const primeFilled = primeKeys.filter(k => canvas[k]).length;
  const canvasFilled = canvasKeys.filter(k => canvas[k]).length;
  const totalFilled = primeFilled + canvasFilled;
  const totalFields = 11;
  const isComplete = totalFilled === totalFields;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      {/* Header toggle */}
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-muted/30"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
          <Flame className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-display text-sm text-foreground tracking-tight">
              PRIME Your CANVAS™ <span className="font-body text-[11px] font-semibold uppercase tracking-widest text-primary">— Optional</span>
            </p>
            {isComplete && (
              <span className="rounded-full border border-border bg-accent px-2 py-0.5 font-body text-[10px] text-accent-foreground font-semibold">
                Complete
              </span>
            )}
            {totalFilled > 0 && !isComplete && (
              <span className="rounded-full border border-border bg-muted px-2 py-0.5 font-body text-[10px] text-muted-foreground">
                {totalFilled}/{totalFields}
              </span>
            )}
          </div>
          {!open && (
            <p className="font-body text-[11px] text-muted-foreground mt-0.5">
              {totalFilled === 0
                ? 'Skip it if you like — photos and your question below are enough'
                : 'Canvas pre-filled · tap to edit'}
            </p>
          )}
          {!open && <SummaryBadges canvas={canvas} />}
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      {/* Expanded body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="prime-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 flex flex-col gap-5 border-t border-border">
              <p className="font-body text-[11px] text-muted-foreground leading-relaxed pt-1">
                Optional — filling these in lets Hair Pro Encyclopedia.ai skip the clarifying questions and deliver a formula immediately. You can also skip this and just upload photos or type your question.
              </p>

              <div className="rounded-xl border border-primary/15 bg-primary/[0.03] px-4 py-3">
                <p className="font-body text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
                  How to read it
                </p>
                <p className="font-body text-xs text-muted-foreground leading-relaxed">
                  Assess <span className="font-semibold text-foreground">P</span>resent Condition,{' '}
                  <span className="font-semibold text-foreground">R</span>eality,{' '}
                  <span className="font-semibold text-foreground">I</span>ntegrity,{' '}
                  <span className="font-semibold text-foreground">M</span>ap Possibilities, and{' '}
                  <span className="font-semibold text-foreground">E</span>xecution. Then study the CANVAS:{' '}
                  <span className="font-semibold text-foreground">C</span>urrent Level,{' '}
                  <span className="font-semibold text-foreground">A</span>rtificial Pigment,{' '}
                  <span className="font-semibold text-foreground">N</span>atural Base,{' '}
                  <span className="font-semibold text-foreground">V</span>isual Texture,{' '}
                  <span className="font-semibold text-foreground">A</span>im (DR),{' '}
                  <span className="font-semibold text-foreground">S</span>trategy.
                </p>
              </div>

              {/* ── PRIME ── */}
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-border" />
                <span className="font-display text-[10px] uppercase tracking-[0.2em] text-muted-foreground">PRIME</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <SelectRow
                letter="P" label="Porosity"
                options={POROSITY_OPTS}
                value={canvas.porosity}
                onChange={set('porosity')}
              />
              <SelectRow
                letter="R" label="Resistance / History"
                options={RESISTANCE_OPTS}
                value={canvas.resistance}
                onChange={set('resistance')}
              />
              <SelectRow
                letter="I" label="Integrity / Condition"
                options={INTEGRITY_OPTS}
                value={canvas.integrity}
                onChange={set('integrity')}
              />
              <SelectRow
                letter="M" label="Map Possibilities (Melanin Base)"
                options={MELANIN_OPTS}
                value={canvas.melaninBase}
                onChange={set('melaninBase')}
              />
              <SelectRow
                letter="E" label="Execution (End Goal)"
                options={GOAL_OPTS}
                value={canvas.endGoal}
                onChange={set('endGoal')}
              />

              {/* ── CANVAS ── */}
              <div className="flex items-center gap-2 mt-2">
                <div className="h-px flex-1 bg-border" />
                <span className="font-display text-[10px] uppercase tracking-[0.2em] text-muted-foreground">CANVAS</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <SelectRow
                letter="C" label="Current Level (CL)"
                options={CURRENT_LEVEL_OPTS}
                value={canvas.currentLevel}
                onChange={set('currentLevel')}
              />
              <SelectRow
                letter="A" label="Artificial Pigment"
                options={ARTIFICIAL_PIGMENT_OPTS}
                value={canvas.artificialPigment}
                onChange={set('artificialPigment')}
              />
              <SelectRow
                letter="N" label="Natural Base"
                options={NATURAL_BASE_OPTS}
                value={canvas.naturalBase}
                onChange={set('naturalBase')}
              />
              <SelectRow
                letter="V" label="Visual Texture / Porosity"
                options={VISUAL_TEXTURE_OPTS}
                value={canvas.visualTexture}
                onChange={set('visualTexture')}
              />
              <SelectRow
                letter="A" label="Aim — Desired Reality (DR)"
                options={AIM_OPTS}
                value={canvas.aim}
                onChange={set('aim')}
              />
              <SelectRow
                letter="S" label="Strategy"
                options={STRATEGY_OPTS}
                value={canvas.strategy}
                onChange={set('strategy')}
              />

              {isComplete && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-border bg-muted/40 px-4 py-3 flex items-start gap-2"
                >
                  <Flame className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary" />
                  <p className="font-body text-xs text-foreground leading-relaxed">
                    PRIME + CANVAS complete. Hair Pro Encyclopedia.ai will analyze your full diagnostic and deliver a direct formula without asking questions.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
